import React, { useState, useEffect, useRef } from 'react';
import type { MoFamily, VideoFile } from './types';
import { formatDate } from '../../utils/datetime';
import { moFamilyStore } from '../../stores/moFamilyStore';
import { SlotSetManager } from '../../components/SlotSetManager';
import { SlotManagementTable } from '../../components/SlotManagementTable';
import { FamilyAvatar } from '../../components/FamilyAvatar';
import { useAppStore } from '../../App';
import { HierarchyTab } from './HierarchyTab';
import { InviteTab } from './InviteTab';
import { SettingsTab } from './SettingsTab';
import { ScreensTab } from './ScreensTab';
import { ScheduleTab } from './ScheduleTab';
import MarketRegisterModal from './MarketRegisterModal';

interface FamilyDetailViewProps {
 selectedFamily: MoFamily;
 hierarchyData: any;
 familyVideos: VideoFile[];
 myVideos: VideoFile[];
 parentFamily: any;
 userOwnedFamilies: MoFamily[];
 onNavigate: (path: string) => void;
 onFamilyUpdated: (familyHash: string) => void;
 onFamilyVideosChanged: (familyHash: string) => void;
}


/** "운영자: …" 한 줄 — 나는 "나", 나 외 운영자가 있으면 남색 굵게(파트너가 영상을 넣는 모패밀리임을 바로 알게). */
const OperatorsLine: React.FC<{ family: MoFamily }> = ({ family }) => {
 const me = useAppStore(s => s.user?.moPeerHash);
 const ops = family.operators ?? [];
 const names = [
  ...ops.filter(o => o.moPeerHash === me).map(() => '나'),
  ...ops.filter(o => o.moPeerHash !== me).map(o => o.name).sort(),
 ];
 const mineOnly = names.length > 0 && names.every(n => n === '나');
 return (
  <p className="text-sm text-gray-500">
   <span className="font-medium">운영자:</span>{' '}
   <span className={mineOnly || names.length === 0 ? '' : 'text-indigo-700 font-medium'}>
    {names.length === 0 ? '없음 (슬롯 없음)' : names.join(', ')}
   </span>
   {!mineOnly && names.length > 0 && (
    <span className="ml-1 text-xs text-gray-400">— 이 모패밀리 슬롯에 영상을 넣는 쪽</span>
   )}
  </p>
 );
};

export const FamilyDetailView: React.FC<FamilyDetailViewProps> = ({
 selectedFamily,
 hierarchyData,
 familyVideos,
 myVideos,
 parentFamily,
 userOwnedFamilies,
 onNavigate,
 onFamilyUpdated,
 onFamilyVideosChanged,
}) => {
 const [activeTab, setActiveTab] = useState<'hierarchy' | 'schedule' | 'screens' | 'invite' | 'slots' | 'settings'>('hierarchy');
 const [isProcessingSlot, setIsProcessingSlot] = useState(false);
 const [showMarketModal, setShowMarketModal] = useState(false);
 const [editingListing, setEditingListing] = useState<any | null>(null);
 const [familyListings, setFamilyListings] = useState<any[]>([]);
 const [imageUploading, setImageUploading] = useState(false);
 const [barterInfo, setBarterInfo] = useState<{ family_type: string; barter_pair_id: string | null } | null>(null);
 const imageInputRef = useRef<HTMLInputElement>(null);

 useEffect(() => {
  if (!selectedFamily.moFamilyHash) return;
  fetch(`/mofamily/${selectedFamily.moFamilyHash}/barter-info`, { credentials: 'include' })
   .then(r => r.json())
   .then(j => { if (j.success) setBarterInfo(j.data); })
   .catch(() => setBarterInfo(null));
 }, [selectedFamily.moFamilyHash]);

 const isBarter = barterInfo?.family_type === 'barter';
 const barterPaired = isBarter && !!barterInfo?.barter_pair_id;

 const endBarterAction = async () => {
  if (!confirm('바터 협업을 종료하면 짝꿍 모패밀리도 함께 종료됩니다. 진행할까요?')) return;
  try {
   const r = await fetch(`/mofamily/${selectedFamily.moFamilyHash}/end-barter`, {
    method: 'POST',
    credentials: 'include',
   });
   const j = await r.json();
   if (!j.success) { alert(j.error || '종료 실패'); return; }
   alert('바터 협업이 종료되었습니다.');
   onNavigate('/mofamily');
  } catch (e: any) {
   alert(e.message || '종료 실패');
  }
 };

 const reloadFamilyListings = async () => {
  if (!selectedFamily.isOwner) return;
  try {
   const r = await fetch('/market/listings/me/list', { credentials: 'include' });
   const j = await r.json();
   if (j.success) {
    setFamilyListings((j.data ?? []).filter((l: any) => l.mo_family_hash === selectedFamily.moFamilyHash));
   }
  } catch (e) {
   console.error('Family listings fetch error:', e);
  }
 };

 useEffect(() => {
  reloadFamilyListings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [selectedFamily.moFamilyHash, selectedFamily.isOwner]);

 const toggleListingActive = async (listing: any) => {
  try {
   const r = await fetch(`/market/listings/${listing.listing_hash}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ is_active: !listing.is_active }),
   });
   const j = await r.json();
   if (!j.success) { alert(j.error || '변경 실패'); return; }
   await reloadFamilyListings();
  } catch (e) {
   console.error('Listing toggle error:', e);
   alert('변경 실패');
  }
 };

 const deleteListing = async (listing: any) => {
  if (!confirm(`"${listing.title}" 마켓 등록을 삭제하시겠습니까?`)) return;
  try {
   const r = await fetch(`/market/listings/${listing.listing_hash}`, {
    method: 'DELETE',
    credentials: 'include',
   });
   const j = await r.json();
   if (!j.success) { alert(j.error || '삭제 실패'); return; }
   await reloadFamilyListings();
  } catch (e) {
   console.error('Listing delete error:', e);
   alert('삭제 실패');
  }
 };

 const sideShortLabel = (side: string) =>
  side === 'seeking_advertiser' ? '광고 받기' :
  side === 'seeking_media' ? '미디어 찾기' :
  side === 'seeking_member' ? '구성원 모집' : side;

 const uploadFamilyImage = async (file: File | undefined) => {
  if (!file || !selectedFamily.isOwner) return;
  setImageUploading(true);
  try {
   const fd = new FormData();
   fd.append('image', file);
   const r = await fetch(`/mofamily/${selectedFamily.moFamilyHash}/image`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
   });
   const data = await r.json();
   if (!data.success) {
    alert(data.error || '업로드 실패');
    return;
   }
   await onFamilyUpdated(selectedFamily.moFamilyHash);
  } catch (e) {
   console.error('Family image upload error:', e);
   alert('업로드 실패');
  } finally {
   setImageUploading(false);
   if (imageInputRef.current) imageInputRef.current.value = '';
  }
 };

 const deleteFamilyImage = async () => {
  if (!selectedFamily.isOwner) return;
  if (!confirm('대표 이미지를 삭제하시겠습니까?')) return;
  try {
   const r = await fetch(`/mofamily/${selectedFamily.moFamilyHash}/image`, {
    method: 'DELETE',
    credentials: 'include',
   });
   const data = await r.json();
   if (!data.success) {
    alert(data.error || '삭제 실패');
    return;
   }
   await onFamilyUpdated(selectedFamily.moFamilyHash);
  } catch (e) {
   console.error('Family image delete error:', e);
   alert('삭제 실패');
  }
 };

 // Reset tab to hierarchy if current tab is not accessible for non-owners
 useEffect(() => {
 if (selectedFamily.role !== 'owner' && (activeTab === 'invite' || activeTab === 'slots' || activeTab === 'settings')) {
 setActiveTab('hierarchy');
 }
 }, [selectedFamily]);

 // Load my videos when hierarchy tab is active
 useEffect(() => {
 if (activeTab !== 'invite') {
 // Reset handled inside InviteTab
 }
 }, [activeTab]);

 // Remove slot
 const removeSlot = async (slotNumber: number) => {
 if (!confirm(`슬롯 #${slotNumber}를 삭제하시겠습니까?`)) {
 return;
 }

 setIsProcessingSlot(true);
 try {
 const activeSetName = moFamilyStore.getState().editingSlotSet || 'A';

 const response = await fetch(`/mofamily/${selectedFamily.moFamilyHash}/slot-sets/${activeSetName}/slots/${slotNumber}`, {
 method: 'DELETE',
 credentials: 'include',
 });

 if (response.ok) {
 // Reload slot sets to reflect the changes
 const setsResponse = await fetch(`/mofamily/${selectedFamily.moFamilyHash}/slot-sets`, {
 credentials: 'include',
 });

 if (setsResponse.ok) {
 const setsData = await setsResponse.json();
 moFamilyStore.getState().setSlotSets(selectedFamily.moFamilyHash, setsData.data.sets);
 }

 // Show success notification
 const notification = document.createElement('div');
 notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 shadow-lg z-50';
 notification.textContent = '슬롯이 삭제되었습니다.';
 document.body.appendChild(notification);
 setTimeout(() => notification.remove(), 3000);

 // Refresh family details
 await onFamilyUpdated(selectedFamily.moFamilyHash);
 } else {
 const error = await response.json();
 alert(error.error || '슬롯 삭제에 실패했습니다.');
 }
 } catch (error) {
 alert('네트워크 오류가 발생했습니다.');
 } finally {
 setIsProcessingSlot(false);
 }
 };

 return (
 <div>
 <div className="mb-3 md:mb-6">
 <button
 onClick={() => onNavigate('/mofamily')}
 className="text-blue-600 hover:text-blue-800 mb-2 md:mb-4 flex items-center gap-2 text-sm md:text-base"
 >
 <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
 </svg>
 목록으로
 </button>

 <div className="bg-white px-1 py-3 md:p-6">
 <div className="flex justify-between items-start mb-3 md:mb-6 gap-2">
 <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
 <div className="relative flex-shrink-0">
  <FamilyAvatar
   name={selectedFamily.name}
   familyHash={selectedFamily.moFamilyHash}
   imageUrl={selectedFamily.imageUrl}
   size={128}
  />
  {selectedFamily.isOwner && (
   <>
    <input
     ref={imageInputRef}
     type="file"
     accept="image/*"
     className="hidden"
     onChange={e => uploadFamilyImage(e.target.files?.[0])}
    />
    <button
     type="button"
     onClick={() => imageInputRef.current?.click()}
     disabled={imageUploading}
     className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center shadow border border-white disabled:opacity-50"
     title={selectedFamily.imageUrl ? '대표 이미지 변경' : '대표 이미지 등록'}
     aria-label={selectedFamily.imageUrl ? '대표 이미지 변경' : '대표 이미지 등록'}
    >
     {imageUploading ? (
      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
       <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
       <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
     ) : (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
       <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 0 1 2-2h2.5l1.5-2h6l1.5 2H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z" />
       <circle cx="12" cy="13" r="3.5" />
      </svg>
     )}
    </button>
    {selectedFamily.imageUrl && (
     <button
      type="button"
      onClick={deleteFamilyImage}
      className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs flex items-center justify-center shadow border border-white"
      title="대표 이미지 삭제"
      aria-label="대표 이미지 삭제"
     >
      ×
     </button>
    )}
   </>
  )}
 </div>
 <div className="flex-1 min-w-0">
 <h1 className="text-lg md:text-2xl font-bold break-words">{selectedFamily.name}</h1>
 {selectedFamily.description && (
 <p className="text-gray-600 mt-2">{selectedFamily.description}</p>
 )}
 <div className="mt-3 space-y-1">
 <p className="text-sm text-gray-500">
 <span className="font-medium">소유자:</span>{' '}
 <span
 className="group relative inline-block cursor-pointer"
 onClick={() => {
 if (selectedFamily.ownerMoPeerHash) {
 navigator.clipboard.writeText(selectedFamily.ownerMoPeerHash);
 alert('소유자 해시가 클립보드에 복사되었습니다.');
 }
 }}
 >
 <span className="hover:text-blue-600 hover:underline">
 {selectedFamily.ownerName || '알 수 없음'}
 </span>
 {selectedFamily.ownerMoPeerHash && (
 <span className="hidden group-hover:block absolute -top-8 left-0 px-2 py-1 text-xs bg-gray-800 text-white whitespace-nowrap">
 클릭하여 해시 복사: {selectedFamily.ownerMoPeerHash.substring(0, 16)}...
 </span>
 )}
 </span>
 </p>
 {/* 운영자 — 이 모패밀리의 슬롯에 영상을 넣는 쪽(소유주와 다를 수 있다: 위임·바터). 목록 카드와 같은 정의. */}
 <OperatorsLine family={selectedFamily} />
 <p className="text-sm text-gray-500">
 <span className="font-medium">생성일:</span> {formatDate(selectedFamily.createdAt)}
 </p>
 <p className="text-sm text-gray-500">
 <span className="font-medium">슬롯:</span> {selectedFamily.totalSlots || 0}개
 </p>
 </div>
 </div>
 </div>
 <div className="flex flex-col items-end gap-1 flex-shrink-0">
 <span className={`px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-sm ${
 selectedFamily.role === 'owner' ? 'bg-purple-100 text-purple-800' :
 selectedFamily.role === 'admin' ? 'bg-blue-100 text-blue-800' :
 'bg-gray-100 text-gray-800'
 }`}>
 {selectedFamily.role === 'owner' ? '소유자' :
 selectedFamily.role === 'admin' ? '관리자' : '멤버'}
 </span>
 {isBarter && (
 <div className="flex items-center gap-1">
 <span className={`px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-sm ${
 barterPaired ? 'bg-purple-200 text-purple-900' : 'bg-purple-50 text-purple-700 border border-purple-200'
 }`}>
 {barterPaired ? '바터 활성' : '바터 (매칭 대기)'}
 </span>
 <span className="relative inline-block group">
 <span className="text-blue-500 cursor-help text-sm">?</span>
 <div className="hidden group-hover:block absolute bottom-full right-0 w-64 p-3 mb-2 text-xs bg-gray-800 text-white z-10">
 바터는 물물교환을 뜻하고, 내 슬롯을 상대에게 위임하고, 상대 슬롯을 위임 받습니다.
 <div className="absolute -bottom-2 right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
 </div>
 </span>
 </div>
 )}
 {selectedFamily.role === 'owner' && !isBarter && (
 <button
 onClick={() => setShowMarketModal(true)}
 className="text-xs md:text-sm px-2 md:px-3 py-0.5 md:py-1 bg-blue-500 hover:bg-blue-600 text-white"
 >
 마켓 등록
 </button>
 )}
 {selectedFamily.role === 'owner' && barterPaired && (
 <div className="flex items-center gap-1">
  <button
   onClick={endBarterAction}
   className="text-xs md:text-sm px-2 md:px-3 py-0.5 md:py-1 bg-red-500 hover:bg-red-600 text-white"
  >
   바터 협업 종료
  </button>
  <span className="relative inline-block group">
   <span className="text-blue-500 cursor-help text-sm">?</span>
   <div className="hidden group-hover:block absolute bottom-full right-0 w-64 p-3 mb-2 text-xs bg-gray-800 text-white z-10">
    종료하면 짝 모패밀리도 자동으로 함께 비활성화됩니다. 양쪽 가상 모패밀리 (자기·짝의 바터·짝 4개), 위임 관계, 마켓 매물 모두 같이 정리되며, 거래 내역은 closed 상태로 보존됩니다.
    <div className="absolute -bottom-2 right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
   </div>
  </span>
 </div>
 )}
 </div>
 </div>

 {selectedFamily.isOwner && familyListings.length > 0 && (
 <div className="bg-gray-50 border border-gray-200 px-3 py-2 mb-4">
 <h3 className="text-sm font-semibold text-gray-700 mb-2">마켓 등록 ({familyListings.length})</h3>
 <ul className="divide-y divide-gray-200">
 {familyListings.map(l => (
 <li key={l.listing_hash} className="py-2 flex items-center gap-2 text-sm flex-wrap">
 <span className={`text-[10px] px-1.5 py-0.5 ${l.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
 {l.is_active ? '활성' : '비활성'}
 </span>
 <span className="text-xs text-gray-500">{sideShortLabel(l.side)}</span>
 <span className="flex-1 truncate font-medium" title={l.title}>{l.title}</span>
 <span className="text-xs text-gray-400">{String(l.period_start).slice(0,10)} ~ {String(l.period_end).slice(0,10)}</span>
 <button
 onClick={() => toggleListingActive(l)}
 className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200"
 >
 {l.is_active ? '끄기' : '켜기'}
 </button>
 <button
 onClick={() => { setEditingListing(l); setShowMarketModal(true); }}
 className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200"
 >
 수정
 </button>
 <button
 onClick={() => deleteListing(l)}
 className="px-2 py-1 text-xs bg-red-50 text-red-600 hover:bg-red-100"
 >
 삭제
 </button>
 </li>
 ))}
 </ul>
 </div>
 )}

 {showMarketModal && (
 <MarketRegisterModal
 moFamilyHash={selectedFamily.moFamilyHash}
 editing={editingListing}
 onClose={() => { setShowMarketModal(false); setEditingListing(null); }}
 onCreated={async () => {
 alert(editingListing ? '마켓 등록을 저장했습니다.' : '마켓에 등록되었습니다.');
 await reloadFamilyListings();
 }}
 />
 )}

 {/* Tabs */}
 <div className="border-b mb-3 md:mb-6">
 <nav className="-mb-px flex justify-between md:justify-start gap-1 md:gap-8 overflow-x-auto">
 <button
 className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5 whitespace-nowrap ${
 activeTab === 'hierarchy'
 ? 'border-blue-500 text-blue-600'
 : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
 }`}
 onClick={() => setActiveTab('hierarchy')}
 >
 <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="3" width="6" height="5" rx="1"/><rect x="3" y="16" width="6" height="5" rx="1"/><rect x="15" y="16" width="6" height="5" rx="1"/><path d="M12 8v4M6 16v-2h12v2"/></svg>
 <span className="sr-only md:not-sr-only">계층도</span>
 </button>
 <button
 className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5 whitespace-nowrap ${
 activeTab === 'schedule'
 ? 'border-blue-500 text-blue-600'
 : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
 }`}
 onClick={() => setActiveTab('schedule')}
 title="이 스크린에 언제 무엇이 재생되는지 한눈에 — 원치 않는 영상은 개별 제외"
 >
 <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18M7 13h5M7 17h9"/></svg>
 <span className="sr-only md:not-sr-only">스케줄</span>
 </button>
 <button
 className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5 whitespace-nowrap ${
 activeTab === 'screens'
 ? 'border-blue-500 text-blue-600'
 : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
 }`}
 onClick={() => setActiveTab('screens')}
 >
 <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="13" rx="1"/><path d="M9 21h6M12 17v4"/></svg>
 <span className="sr-only md:not-sr-only">스크린</span>
 </button>
 {selectedFamily.role === 'owner' && (
 <button
 className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5 whitespace-nowrap ${
 activeTab === 'invite'
 ? 'border-blue-500 text-blue-600'
 : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
 } ${selectedFamily?.maxMembers === 1 ? 'cursor-not-allowed opacity-50' : ''}`}
 onClick={() => {
 if (selectedFamily?.maxMembers !== 1) {
 setActiveTab('invite');
 }
 }}
 disabled={selectedFamily?.maxMembers === 1}
 >
 <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2"/><path d="M3.5 20a5.5 5.5 0 0 1 11 0"/><path d="M19 7v6M16 10h6"/></svg>
 <span className="sr-only md:not-sr-only">모패밀리 초대</span>{selectedFamily?.maxMembers === 1 && <span className="hidden md:inline text-xs">(개인 전용)</span>}
 </button>
 )}
 {selectedFamily.role === 'owner' && (
 <button
 className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5 whitespace-nowrap ${
 activeTab === 'slots'
 ? 'border-blue-500 text-blue-600'
 : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
 }`}
 onClick={() => setActiveTab('slots')}
 >
 <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="6" height="6" rx="1"/><rect x="14" y="4" width="6" height="6" rx="1"/><rect x="4" y="14" width="6" height="6" rx="1"/><rect x="14" y="14" width="6" height="6" rx="1"/></svg>
 <span className="sr-only md:not-sr-only">슬롯 관리</span>
 </button>
 )}
 {selectedFamily.role === 'owner' && (
 <button
 className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5 whitespace-nowrap ${
 activeTab === 'settings'
 ? 'border-blue-500 text-blue-600'
 : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
 }`}
 onClick={() => setActiveTab('settings')}
 >
 <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
 <span className="sr-only md:not-sr-only">설정</span>
 </button>
 )}
 </nav>
 </div>

 {/* Tab Content */}
 {activeTab === 'hierarchy' && (
 <HierarchyTab
 selectedFamily={selectedFamily}
 hierarchyData={hierarchyData}
 familyVideos={familyVideos}
 myVideos={myVideos}
 parentFamily={parentFamily}
 userOwnedFamilies={userOwnedFamilies}
 onFamilyUpdated={onFamilyUpdated}
 onNavigate={onNavigate}
 onFamilyVideosChanged={onFamilyVideosChanged}
 />
 )}



 {activeTab === 'schedule' && (
 <ScheduleTab familyHash={selectedFamily.moFamilyHash} />
 )}

 {activeTab === 'screens' && (
 <ScreensTab moFamilyHash={selectedFamily.moFamilyHash} />
 )}

 {activeTab === 'invite' && (
 <InviteTab
 selectedFamily={selectedFamily}
 onTabChange={(tab) => setActiveTab(tab as any)}
 />
 )}

 {activeTab === 'slots' && selectedFamily.role === 'owner' && (
 <div className="space-y-4">
 <div className="bg-gray-50 p-4">
 <div className="flex justify-between items-center mb-4">
 <h4 className="text-lg font-medium">슬롯 관리</h4>
 </div>

 <SlotManagementTable familyHash={selectedFamily.moFamilyHash} onRemoveSlot={removeSlot} />

 {/* Multi-slot system manager */}
 <SlotSetManager
 familyHash={selectedFamily.moFamilyHash}
 isOwner={selectedFamily.isOwner}
 />

 <p className="text-sm text-gray-500 mt-4">
 * 슬롯은 영상 재생 스케줄의 구좌를 의미합니다. 각 슬롯에 영상을 할당할 수 있습니다.
 </p>
 </div>
 </div>
 )}

 {activeTab === 'settings' && (
 <SettingsTab
 selectedFamily={selectedFamily}
 onFamilyUpdated={onFamilyUpdated}
 onNavigate={onNavigate}
 />
 )}
 </div>
 </div>
 </div>
 );
};
